import type { ReactNode } from "react";
import { cn } from "../../lib/utils.ts";
import { WaxSeal } from "../brand/wax-seal.tsx";
import { Button } from "../ui/button.tsx";

export interface StateScreenProps {
  /** Glyph rendered inside the seal emblem; defaults to the brand mark. */
  icon?: ReactNode;
  title: string;
  body?: string;
  tone?: "gold" | "lime";
  action?: string;
  onAction?: () => void;
  secondary?: string;
  onSecondary?: () => void;
  className?: string;
}

/**
 * Full-screen branded state (venue closed, private, empty room, order failed…).
 * Friendly, low-stress, always rooted on the wax-seal emblem.
 */
export function StateScreen({
  icon,
  title,
  body,
  tone = "gold",
  action,
  onAction,
  secondary,
  onSecondary,
  className,
}: StateScreenProps) {
  return (
    <div
      className={cn(
        "flex h-full flex-col items-center justify-center gap-1 px-8 text-center",
        className,
      )}
    >
      <div className="relative mb-5 grid place-items-center">
        <WaxSeal tone={tone} />
        {icon ? (
          <span
            className="absolute"
            style={{ color: tone === "lime" ? "var(--lime-500)" : "var(--gold-500)" }}
          >
            {icon}
          </span>
        ) : null}
      </div>
      <h2 className="h-display text-[1.4rem]">{title}</h2>
      {body ? (
        <p className="mt-1.5 max-w-[17rem] text-cream-2 text-sm leading-relaxed">{body}</p>
      ) : null}
      {action ? (
        <div className="mt-5 w-full max-w-[15.5rem]">
          <Button
            variant={tone === "lime" ? "accent" : "default"}
            className="w-full"
            onClick={onAction}
          >
            {action}
          </Button>
        </div>
      ) : null}
      {secondary ? (
        <button
          type="button"
          onClick={onSecondary}
          className="mt-2.5 font-medium text-cream-3 text-sm transition-colors hover:text-cream-2"
        >
          {secondary}
        </button>
      ) : null}
    </div>
  );
}
